import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Navigation from '../components/Navigation'
import Logo from '../components/Logo'
import { apiClient } from '../services/apiClient'

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [success, setSuccess] = useState<boolean>(false)
  const [tokenValid, setTokenValid] = useState<boolean | null>(null)

  const token = searchParams.get('token')
  const email = searchParams.get('email')

  useEffect(() => {
    // Check if token and email are present
    if (!token || !email) {
      setTokenValid(false)
      setError('Invalid or missing reset token')
    } else {
      setTokenValid(true)
    }
  }, [token, email])

  const validateForm = (): boolean => {
    if (!password) {
      setError('Password is required')
      return false
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long')
      return false
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!validateForm() || !token || !email) return

    setLoading(true)
    try {
      await apiClient.resetPassword(token, email, password)
      setSuccess(true)
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('expired') || error.message.includes('invalid')) {
          setError('This reset link has expired or is invalid. Please request a new password reset.')
        } else {
          setError(error.message)
        }
      } else {
        setError('Failed to reset password. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // Token validation failed
  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-firedup-dark">
        <Navigation />
        
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <Logo className="w-20 h-20 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-white mb-2">Invalid Reset Link</h1>
            </div>

            <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4">Link Invalid or Expired</h3>
                
                <p className="text-gray-300 mb-6">
                  This password reset link is invalid or has expired. Reset links are only valid for 1 hour 
                  after they're sent for security purposes.
                </p>

                <div className="space-y-3">
                  <Link 
                    to="/forgot-password" 
                    className="block w-full btn-primary text-center"
                  >
                    Request New Reset Link
                  </Link>
                  
                  <Link 
                    to="/login" 
                    className="block w-full btn-secondary text-center"
                  >
                    Back to Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-firedup-dark">
        <Navigation />
        
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <Logo className="w-20 h-20 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-white mb-2">Password Reset Successful</h1>
            </div>

            <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4">Password Updated!</h3>
                
                <p className="text-gray-300 mb-6">
                  Your password has been successfully reset. You can now sign in with your new password.
                </p>

                <Link 
                  to="/login" 
                  className="block w-full btn-primary text-center"
                >
                  Sign In Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Reset form
  return (
    <div className="min-h-screen bg-firedup-dark">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <Logo className="w-20 h-20 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Reset Your Password</h1>
            <p className="text-gray-400">Enter your new password below</p>
          </div>

          <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 text-red-300 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                  New Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className={`input-field ${error && password ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Enter your new password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    if (error) setError('')
                  }}
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Password must be at least 6 characters long
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className={`input-field ${error && confirmPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    if (error) setError('')
                  }}
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading || !password || !confirmPassword}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </button>

              <div className="text-center">
                <Link 
                  to="/login" 
                  className="text-gray-400 hover:text-gray-300 transition-colors text-sm"
                >
                  Back to Login
                </Link>
              </div>
            </form>

            {/* Security Notice */}
            <div className="mt-6 p-4 bg-firedup-dark/50 rounded border border-firedup-light-gray">
              <h4 className="text-sm font-medium text-gray-300 mb-2">Security Notice</h4>
              <p className="text-xs text-gray-400">
                After resetting your password, you'll be automatically signed out of all devices 
                for security purposes. You'll need to sign in again with your new password.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
